import { useMemo } from 'react';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { DistributorID, ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { useGetCatalogQuery, useGetServiceConnectionsQuery } from '../../../../redux/api/fymAPI';

/** The streaming sources that fill Streaming (design your-intelligence h2). */
export const CATALOG_STREAMING_SOURCES: readonly string[] = [
  MusicAPIIntegrationID.Spotify,
  MusicAPIIntegrationID.Deezer,
  MusicAPIIntegrationID.YoutubeMusic,
  MusicAPIIntegrationID.SoundCloud,
  DistributorID.Ditto,
];

/** The fields of a connection card that the badge reads. */
export interface CatalogBadgeConnection {
  integrationType: string;
  status: ServiceConnectionStatus;
  artist: object | null;
}

/** What the setup dot of the Catalog asks for: the first song, or the artist of a login. */
export type CatalogSetupMissing = 'release' | 'artist';

/**
 * What the Catalog item shows. `reconnect`: the count of streaming sources that need Reconnect (a
 * red count). `setup`: the hollow setup dot, and `missing` names what it asks for. The count wins
 * when both apply.
 */
export interface CatalogBadgeState {
  setup: boolean;
  missing: CatalogSetupMissing | null;
  reconnect: number;
}

/**
 * The Catalog badge rule (design your-intelligence h2). The setup dot shows when no release is
 * imported, or when a login has no artist chosen. The red count is the number of streaming sources
 * that need Reconnect.
 *
 * QA 2026-10-28 (imp-9, lit-11): a Catalog with a song and no streaming source has no dot. The dot
 * after "Saved to Catalog" read as "the song was not saved". The first-release checklist and
 * Streaming still ask for a source.
 */
export function catalogBadgeState(
  catalogItemCount: number | undefined,
  connections: CatalogBadgeConnection[] | undefined
): CatalogBadgeState {
  const sources = (connections ?? []).filter((connection) =>
    CATALOG_STREAMING_SOURCES.includes(connection.integrationType)
  );
  const reconnect = sources.filter((source) => source.status === ServiceConnectionStatus.expired).length;
  const connected = sources.filter(
    (source) => source.status === ServiceConnectionStatus.connected || source.status === ServiceConnectionStatus.expired
  );
  const noRelease = catalogItemCount === 0;
  const noArtist = connected.some(
    (source) => source.status === ServiceConnectionStatus.connected && source.artist === null
  );
  let missing: CatalogSetupMissing | null = null;
  if (noRelease) {
    missing = 'release';
  } else if (noArtist) {
    missing = 'artist';
  }
  return { setup: missing !== null, missing, reconnect };
}

/**
 * The Catalog badge. It reads the Catalog screen's unfiltered list (`GET /accounts/:id/catalog`)
 * and the connection cards, so it shares their cache entries. Nothing until both load, so the
 * badge never flashes.
 */
export function useCatalogSetupNeeded(accountId: number | null): CatalogBadgeState {
  const skip = accountId === null;
  const { data: catalog } = useGetCatalogQuery({ accountId: accountId ?? 0 }, { skip });
  const { data: connections } = useGetServiceConnectionsQuery({ accountId: accountId ?? 0 }, { skip });

  return useMemo(() => {
    if (catalog === undefined || connections === undefined) {
      return { setup: false, missing: null, reconnect: 0 };
    }
    return catalogBadgeState(catalog.items.length, connections.connections);
  }, [catalog, connections]);
}
